import { SupportedLocales, SupportedSpokenLocales } from '~/configuration';

export type SupportedLocale = (typeof SupportedLocales)[number];
export type SupportedSpokenLocale = (typeof SupportedSpokenLocales)[number];

/**
 * Backoffice stores content and storefront-configured languages using a mix of
 * bare language codes (`en`, `da`, `no`, `sv`, `de`, `it`, `nl`, `es`, `ca`, …)
 * and the one regioned variant it historically exposed (`en-US`). This app,
 * however, only supports the full locales listed in `SupportedLocales` (e.g.
 * `en-GB`, `da-DK`). This derives the bridge between those two worlds: bare
 * code → canonical `SupportedLocales`/`SupportedSpokenLocales` entry.
 *
 * Derivation, not a maintained list: for each locale, its bare code is its
 * language subtag (`da-DK` → `da`). A bare code is only auto-mapped when
 * exactly one locale has it — `en` is ambiguous (`en-GB` vs `en-US`), so it's
 * excluded from the derivation and given an explicit override below instead.
 * Adding a new locale file under `app/domain/i18n/strings/` therefore wires up
 * its bare code automatically; no edit needed here.
 *
 * Kept in sync with `storefrontLanguages` in the backoffice repo
 * (`src/i18n/config.ts`).
 */
function deriveBareLanguageMap<T extends string>(
  locales: readonly T[]
): Record<string, T> {
  const shortCodeCounts = new Map<string, number>();
  for (const locale of locales) {
    const shortCode = locale.split('-')[0]!;
    shortCodeCounts.set(shortCode, (shortCodeCounts.get(shortCode) ?? 0) + 1);
  }

  const map: Record<string, T> = {};
  for (const locale of locales) {
    const shortCode = locale.split('-')[0]!;
    if (shortCodeCounts.get(shortCode) === 1) {
      map[shortCode] = locale;
    }
  }
  return map;
}

const BARE_LANGUAGE_TO_LOCALE: Record<string, SupportedLocale> = {
  ...deriveBareLanguageMap(SupportedLocales),
  // `en` alone is ambiguous between en-GB/en-US; bare `en` resolves
  // specifically to en-GB (matches the historical / backoffice convention).
  en: 'en-GB',
};

// Superset of `BARE_LANGUAGE_TO_LOCALE` covering event spoken-language inputs
// only. Kept in sync with backoffice `supportedSpokenLanguages`.
const BARE_SPOKEN_LANGUAGE_TO_LOCALE: Record<string, SupportedSpokenLocale> = {
  ...deriveBareLanguageMap(SupportedSpokenLocales),
  en: 'en-GB',
};

/**
 * Maps any backoffice/storefront/content language tag to the canonical full
 * locale used by this app. Returns `null` when the input can't be mapped to a
 * `SupportedLocales` entry.
 *
 * Client-safe — no server dependencies, safe to import from browser code.
 */
export function normaliseLocale(
  input: string | undefined | null
): SupportedLocale | null {
  if (!input) return null;
  const lowered = input.toLowerCase();

  const exact = SupportedLocales.find((l) => l.toLowerCase() === lowered);
  if (exact) return exact;

  const bare = lowered.split('-')[0];
  return (bare && BARE_LANGUAGE_TO_LOCALE[bare]) ?? null;
}

// Spoken-language counterpart of `normaliseLocale`, for event spoken-language
// tagging (e.g. "this tour is conducted in Catalan") rather than UI locale
// resolution. It resolves against `SupportedSpokenLocales`, which happens to
// equal `SupportedLocales` today (see the comment on that export in
// `~/configuration`) but is intentionally a separate function/constant pair,
// since the two questions — "is this a real spoken-language tag" vs. "is the
// storefront UI translated into this locale" — could diverge again.
export function normaliseSpokenLocale(
  input: string | undefined | null
): SupportedSpokenLocale | null {
  if (!input) return null;
  const lowered = input.toLowerCase();

  const exact = SupportedSpokenLocales.find((l) => l.toLowerCase() === lowered);
  if (exact) return exact;

  const bare = lowered.split('-')[0];
  return (bare && BARE_SPOKEN_LANGUAGE_TO_LOCALE[bare]) ?? null;
}

export function getLanguageCode(locale: string) {
  return new Intl.Locale(locale).language;
}

/**
 * The profile-configured language code to use as the *canonical* URL prefix
 * for a resolved locale — or `undefined` when that's the default language
 * (which has no prefix). E.g. resolved `en-GB` for a company configured with
 * `en` (default `da`) → `'en'`; resolved `da-DK` (the default) → `undefined`.
 *
 * Client-safe — no server dependencies.
 */
export function toConfiguredLocalePrefix(
  resolvedLocale: string,
  languages: string[],
  defaultLanguage: string | undefined
): string | undefined {
  const canonical = normaliseLocale(resolvedLocale);
  if (!canonical) return undefined;
  if (defaultLanguage && normaliseLocale(defaultLanguage) === canonical) {
    return undefined;
  }
  const matchingNormalisedLocale = languages.find(
    (l) => normaliseLocale(l) === canonical
  );
  if (matchingNormalisedLocale) {
    return matchingNormalisedLocale;
  }

  const matchingBareLanguage = languages.find((l) => {
    const [language] = l.toLowerCase().split('-');
    const [resolvedLanguage] = resolvedLocale.toLowerCase().split('-');
    return language === resolvedLanguage;
  });
  return matchingBareLanguage;
}

/**
 * Inverse of `BARE_LANGUAGE_TO_LOCALE`: given a full `SupportedLocale`,
 * return the key shape backoffice/content services expect.
 */
export function toBackofficeLocale(locale: string): string {
  const lowered = locale.toLowerCase();
  for (const [bare, canonical] of Object.entries(BARE_LANGUAGE_TO_LOCALE)) {
    if (canonical.toLowerCase() === lowered) return bare;
  }
  return locale;
}
